import React, { useState } from 'react';
import { Box, Container, Typography, Link, Grid, TextField, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { newsletterService } from '../services/api';

const FOOTER_BG = '#293835';
const FOOTER_BAR_BG = '#212D2B';
const ACCENT_DARK = '#4E605C';
const BUTTON_BG = '#384D49';
const BRAND_ORANGE = '#E85D1C';

const FOOTER_CONTACT = {
  address: '4648 Rocky Road Philadelphia PA, 19120',
  email: 'info@rwfngo.com',
  phoneDisplay: '+88 0123 654 99',
};

const menuLinksLeft = [
  { label: 'Home', to: '/' },
  { label: 'Founders', to: '/founders' },
  { label: 'Financial & Legal Documents', to: '/financial-legal-documents' },
  { label: 'Gallery', to: '/gallery' },
];

const menuLinksRight = [
  { label: 'About', to: '/about' },
  { label: 'Team', to: '/team' },
  { label: 'Campaign', to: '/campaigns' },
  { label: 'Contact', to: '/contact' },
  { label: 'Volunteer', to: '/volunteer' },
];

const Footer = () => {
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState({ type: '', message: '' });
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      setNewsletterStatus({ type: 'error', message: 'Please enter your email address.' });
      return;
    }
    try {
      setNewsletterSubmitting(true);
      setNewsletterStatus({ type: '', message: '' });
      const res = await newsletterService.subscribe(value, 'footer');
      const message = res?.data?.message || 'Thank you for subscribing to our newsletter.';
      setNewsletterStatus({ type: 'success', message });
      setEmail('');
    } catch (error) {
      setNewsletterStatus({
        type: 'error',
        message: error.response?.data?.error || 'Unable to subscribe. Please try again.',
      });
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  return (
    <Box component="footer" sx={{ color: '#FFFFFF', mt: 8, overflow: 'visible' }}>
      {/* Orange contact bar - overlaps onto footer (pulled up into footer area) */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          mx: { xs: 2, sm: 3 },
          mb: 0,
        }}
      >
        <Container maxWidth={false} disableGutters sx={{ width: '100%', maxWidth: 1200 }}>
          <Box
            sx={{
              backgroundColor: BRAND_ORANGE,
              borderRadius: { xs: 3, md: '23px' },
              pt: { xs: 4, md: 5 },
              pb: { xs: 5, md: 6 },
              px: { xs: 3, md: 6 },
              mb: '25px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            }}
          >
            <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="space-around" alignItems="center">
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap' }}>
                  <Box sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <LocationOnIcon sx={{ color: BRAND_ORANGE, fontSize: 28 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 500, fontSize: 12, color: 'rgba(255,255,255,0.95)', mb: 0.5 }}>Address</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 14, color: '#FFFFFF', lineHeight: 1.4 }}>{FOOTER_CONTACT.address}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap' }}>
                  <Box sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EmailIcon sx={{ color: BRAND_ORANGE, fontSize: 28 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 500, fontSize: 12, color: 'rgba(255,255,255,0.95)', mb: 0.5 }}>Send Email</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 14, color: '#FFFFFF', lineHeight: 1.4 }}>{FOOTER_CONTACT.email}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'nowrap' }}>
                  <Box sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: '50%', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PhoneIcon sx={{ color: BRAND_ORANGE, fontSize: 28 }} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 500, fontSize: 12, color: 'rgba(255,255,255,0.95)', mb: 0.5 }}>Call Emergency</Typography>
                    <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: 14, color: '#FFFFFF', lineHeight: 1.4 }}>{FOOTER_CONTACT.phoneDisplay}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Main footer content - negative margin so orange bar overlaps onto it */}
      <Box sx={{ backgroundColor: FOOTER_BG, color: '#FFFFFF', position: 'relative', zIndex: 1, mt: -6, pt: 10, minHeight: 'auto', py: { xs: 5, md: 6 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 4, md: 4 }}>
            {/* Left - Roots & wings + description */}
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontFamily: '"Nunito", sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: 28, sm: 34, md: 40 },
                  lineHeight: '100%',
                  letterSpacing: '1px',
                  color: '#FFFFFF',
                  mb: '25px',
                }}
              >
                Roots & wings
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 300,
                  fontSize: { xs: 13, md: 14 },
                  lineHeight: '37px',
                  letterSpacing: 0,
                  color: '#FFFFFF',
                  maxWidth: { xs: '100%', md: 460 },
                }}
              >
                Roots & Wings Foundation is dedicated to creating positive social impact through community engagement, education, and sustainable development. We empower individuals and communities to achieve their full potential and lead a helping hand to those in need.
              </Typography>
            </Grid>

            {/* Middle - Menu */}
            <Grid item xs={12} md={4} sx={{ pl: { xs: 0, md: '60px' } }}>
              <Typography
                sx={{
                  fontFamily: '"Nunito", sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: '100%',
                  letterSpacing: '1px',
                  color: '#FFFFFF',
                  mb: 2,
                }}
              >
                Menu
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  columnGap: { xs: 2, sm: 4, md: 6 },
                  alignItems: 'start',
                  maxWidth: { xs: '100%', md: 380 },
                }}
              >
                {[menuLinksLeft, menuLinksRight].map((column) => (
                  <Box key={column[0].label} sx={{ display: 'flex', flexDirection: 'column', rowGap: 0.5 }}>
                    {column.map(({ label, to }) => (
                      <Link
                        key={label}
                        component={RouterLink}
                        to={to}
                        sx={{
                          display: 'block',
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: 500,
                          fontSize: 14,
                          lineHeight: '40px',
                          letterSpacing: '0.03em',
                          color: '#FFFFFF',
                          textDecoration: 'none',
                          '&:hover': { color: 'rgba(255,255,255,0.85)' },
                        }}
                      >
                        {label}
                      </Link>
                    ))}
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right - Connect With Us + Newsletter */}
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontFamily: '"Nunito", sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: '100%',
                  letterSpacing: '1px',
                  color: '#FFFFFF',
                  mb: 2,
                }}
              >
                Connect With Us
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                {[
                  { Icon: FacebookIcon, href: 'https://www.facebook.com/', label: 'Facebook' },
                  { Icon: TwitterIcon, href: 'https://twitter.com/', label: 'X (Twitter)' },
                  { Icon: InstagramIcon, href: 'https://www.instagram.com/', label: 'Instagram' },
                  { Icon: YouTubeIcon, href: 'https://www.youtube.com/', label: 'YouTube' },
                ].map(({ Icon, href, label }) => (
                  <Box
                    key={label}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    sx={{
                      width: 45,
                      height: 45,
                      borderRadius: '50%',
                      backgroundColor: ACCENT_DARK,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      textDecoration: 'none',
                      '&:hover': { opacity: 0.9, color: '#FFFFFF' },
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </Box>
                ))}
              </Box>

              <Typography
                sx={{
                  fontFamily: '"Nunito", sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: 16, md: 18 },
                  lineHeight: '100%',
                  letterSpacing: '1px',
                  color: '#FFFFFF',
                  mb: 1.5,
                }}
              >
                News Letter
              </Typography>
              <Box component="form" onSubmit={handleNewsletter} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 }, alignItems: 'stretch', maxWidth: 397 }}>
                <TextField
                  size="small"
                  placeholder="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={newsletterSubmitting}
                  sx={{
                    width: { xs: '100%', md: 302 },
                    flex: { xs: 'none', sm: '0 0 302px' },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: ACCENT_DARK,
                      color: '#FFFFFF',
                      height: 43,
                      fontFamily: '"Nunito", sans-serif',
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: '100%',
                      letterSpacing: '1px',
                      '& fieldset': { border: 'none' },
                      '& input::placeholder': { color: '#FFFFFF', opacity: 1 },
                    },
                  }}
                />
                <Button
                  type="submit"
                  disabled={newsletterSubmitting}
                  sx={{
                    width: { xs: '100%', sm: 95 },
                    height: 43,
                    backgroundColor: BUTTON_BG,
                    color: '#FFFFFF',
                    fontFamily: '"Nunito", sans-serif',
                    fontWeight: 700,
                    fontSize: 14,
                    lineHeight: '100%',
                    letterSpacing: '1px',
                    borderRadius: 0,
                    '&:hover': { backgroundColor: '#2d3d3a' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.7)' },
                  }}
                >
                  {newsletterSubmitting ? '...' : 'Send'}
                </Button>
              </Box>
              {newsletterStatus.message && (
                <Typography sx={{ mt: 1, fontSize: 13, color: newsletterStatus.type === 'error' ? '#ffb4a8' : '#b7f0c4' }}>
                  {newsletterStatus.message}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Copyright bar */}
      <Box
        sx={{
          minHeight: 56,
          py: { xs: 2, md: 0 },
          backgroundColor: FOOTER_BAR_BG,
          display: 'flex',
          alignItems: 'center',
          borderTop: `1px solid ${ACCENT_DARK}`,
        }}
      >
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between', gap: 1.5 }}>
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 300,
              fontSize: { xs: 11, md: 12 },
              lineHeight: '100%',
              letterSpacing: 0,
              color: '#FFFFFF',
            }}
          >
            © Copyright Nandan Info Technologies
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, md: 2.5 } }}>
            {[
              { label: 'Terms & Conditions', to: '/terms' },
              { label: 'Privacy Policy', to: '/privacy' },
              { label: 'Refund and Cancellation Policy', to: '/refund' },
            ].map(({ label, to }) => (
              <Link
                key={to}
                component={RouterLink}
                to={to}
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: 11, md: 12 },
                  color: 'rgba(255,255,255,0.85)',
                  textDecoration: 'none',
                  '&:hover': { color: '#FFFFFF', textDecoration: 'underline' },
                }}
              >
                {label}
              </Link>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
